import { useState, useEffect, useCallback } from "react";
import { APP_BASE_URL } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import {
  UserPlus, Shield, Trash2, Users, FileText, RefreshCw, Building2, Ship, Globe, Plus, Crown, ChevronDown, Pencil,
} from "lucide-react";
import { OrgSettingsModal } from "@/components/OrgSettingsModal";
import { CsvUploadCard } from "@/components/CsvUploadCard";
import { CsvUploadReglasCard } from "@/components/CsvUploadReglasCard";
import { useToast } from "@/hooks/use-toast";
import { Navigate, useNavigate } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppShell } from "@/components/layout/AppShell";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";

/* ─── Types ─── */
interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  credits_this_month: number;
  total_documents: number;
}

interface GlobalStats {
  total_orgs: number;
  total_users: number;
  total_docs: number;
  total_embarques: number;
}

interface OrgRow {
  id: string;
  nombre: string;
  nit: string | null;
  created_at: string;
  member_count: number;
  plan_type: string;
  monthly_doc_limit: number;
}

const PLAN_LIMITS: Record<string, number> = { BASIC: 100, PRO: 500, ENTERPRISE: 1000 };
const PLAN_LABELS: Record<string, { label: string; className: string }> = {
  BASIC: { label: "Basic", className: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" },
  PRO: { label: "Pro", className: "border-primary/40 text-primary bg-primary/10" },
  ENTERPRISE: { label: "Enterprise", className: "border-purple-500/40 text-purple-400 bg-purple-500/10" },
};

interface GlobalUser {
  user_id: string;
  email: string;
  full_name: string | null;
  org_name: string;
  rol: string;
  created_at: string;
}

const displayName = (u: { full_name?: string | null; email?: string | null }) =>
  (u.full_name?.trim() || u.email?.split("@")[0] || "—");

/* ─── Helpers ─── */
const formatDate = (d: string | null) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
};

/* ═══════════════════════════════════════════════════
   SUPER ADMIN VIEW
   ═══════════════════════════════════════════════════ */
function SuperAdminView() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  /* ── Delete Org ── */
  const [deleteOrgTarget, setDeleteOrgTarget] = useState<OrgRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingOrg, setDeletingOrg] = useState(false);
  const [editOrgTarget, setEditOrgTarget] = useState<OrgRow | null>(null);

  const expectedDeleteText = deleteOrgTarget ? `ELIMINAR ${deleteOrgTarget.nombre}` : "";

  const handleDeleteOrg = async () => {
    if (!deleteOrgTarget || deleteConfirmText !== expectedDeleteText) return;
    setDeletingOrg(true);
    try {
      const { data, error } = await supabase.rpc("delete_organization_cascade", {
        target_org_id: deleteOrgTarget.id,
      });
      if (error) throw error;
      const result = data as any;
      toast({
        title: "🗑️ Organización eliminada",
        description: `"${result.deleted_org}" — ${result.documents_removed} docs, ${result.embarques_removed} embarques, ${result.members_removed} miembros eliminados.`,
      });
      setDeleteOrgTarget(null);
      setDeleteConfirmText("");
      refetchOrgs();
      queryClient.invalidateQueries({ queryKey: ["global-stats"] });
      refetchUsers();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "No se pudo eliminar la organización.", variant: "destructive" });
    }
    setDeletingOrg(false);
  };

  /* ── Stats ── */
  const { data: stats } = useQuery<GlobalStats>({
    queryKey: ["global-stats"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_global_stats");
      if (error) throw error;
      return data as unknown as GlobalStats;
    },
    staleTime: 60_000,
  });

  /* ── Orgs ── */
  const { data: orgs = [], isLoading: orgsLoading, refetch: refetchOrgs } = useQuery<OrgRow[]>({
    queryKey: ["all-organizations"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_all_organizations");
      if (error) throw error;
      return (data ?? []) as unknown as OrgRow[];
    },
  });

  /* ── Doc counts per org (current month) ── */
  const orgIds = orgs.map(o => o.id);
  const { data: docCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["org-doc-counts", orgIds],
    queryFn: async () => {
      const results: Record<string, number> = {};
      await Promise.all(
        orgs.map(async (org) => {
          const { data, error } = await supabase.rpc("get_current_month_doc_count", { _org_id: org.id });
          if (!error) results[org.id] = (data as number) ?? 0;
        })
      );
      return results;
    },
    enabled: orgs.length > 0,
    staleTime: 30_000,
  });

  /* ── Change Plan ── */
  const [changingPlanId, setChangingPlanId] = useState<string | null>(null);
  const handleChangePlan = async (orgId: string, newPlan: string) => {
    setChangingPlanId(orgId);
    try {
      const { error } = await supabase
        .from("organizaciones")
        .update({ plan_type: newPlan, monthly_doc_limit: PLAN_LIMITS[newPlan] ?? 100 })
        .eq("id", orgId);
      if (error) throw error;
      toast({ title: "✅ Plan actualizado", description: `Plan cambiado a ${newPlan} (${PLAN_LIMITS[newPlan]} docs/mes).` });
      refetchOrgs();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setChangingPlanId(null);
  };


  const { data: globalUsers = [], isLoading: usersLoading, refetch: refetchUsers } = useQuery<GlobalUser[]>({
    queryKey: ["all-users-global"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_all_users_global");
      if (error) throw error;
      return (data ?? []) as unknown as GlobalUser[];
    },
  });

  /* ── Legacy user list via edge function ── */
  const [legacyUsers, setLegacyUsers] = useState<AdminUser[]>([]);
  const [loadingLegacy, setLoadingLegacy] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchLegacyUsers = useCallback(async () => {
    setLoadingLegacy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users?action=list", { method: "GET" });
      if (error) throw error;
      if (data?.users) setLegacyUsers(data.users);
    } catch {
      toast({ title: "Error", description: "No se pudieron cargar los usuarios.", variant: "destructive" });
    }
    setLoadingLegacy(false);
  }, [toast]);

  useEffect(() => { fetchLegacyUsers(); }, [fetchLegacyUsers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users?action=create", { body: { email, password } });
      if (error) throw error;
      if (data?.error) {
        toast({ title: "Error", description: data.error, variant: "destructive" });
      } else {
        toast({ title: "✅ Usuario creado", description: data.message });
        setEmail(""); setPassword("");
        fetchLegacyUsers();
        refetchUsers();
      }
    } catch {
      toast({ title: "Error", description: "No se pudo crear el usuario.", variant: "destructive" });
    }
    setCreating(false);
  };

  const handleDelete = async (userId: string) => {
    setDeletingId(userId);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users?action=delete", { body: { user_id: userId } });
      if (error) throw error;
      if (data?.error) {
        toast({ title: "Error", description: data.error, variant: "destructive" });
      } else {
        toast({ title: "Usuario eliminado", description: data.message });
        fetchLegacyUsers();
        refetchUsers();
      }
    } catch {
      toast({ title: "Error", description: "No se pudo eliminar.", variant: "destructive" });
    }
    setDeletingId(null);
  };

  /* ── New Org Modal ── */
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgNit, setNewOrgNit] = useState("");
  const [newOrgOwnerEmail, setNewOrgOwnerEmail] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [showInviteSuccess, setShowInviteSuccess] = useState(false);
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || !newOrgOwnerEmail.trim()) return;
    setCreatingOrg(true);
    try {
      // Find owner user_id by email if provided — if not found, create org with self as owner
      let ownerUserId: string | undefined;
      const found = globalUsers.find(u => u.email === newOrgOwnerEmail.trim());
      if (found) {
        ownerUserId = found.user_id;
      }

      const { data: orgData, error } = await supabase.rpc("create_organization_with_owner", {
        _nombre: newOrgName.trim(),
        _nit: newOrgNit.trim() || null,
        _owner_user_id: ownerUserId ?? null,
      });
      if (error) throw error;

      const orgId = (orgData as any)?.org_id;

      // Generate invitation token
      if (orgId) {
        const { data: invData, error: invError } = await (supabase as any)
          .from("invitaciones")
          .insert({
            org_id: orgId,
            email: newOrgOwnerEmail.trim(),
            rol: "ADMIN",
            created_by: user!.id,
          })
          .select("token")
          .single();

        if (!invError && invData?.token) {
          const link = `${APP_BASE_URL}/onboarding?token=${invData.token}`;
          setInviteLink(link);
          setInviteEmail(newOrgOwnerEmail.trim());
          setShowInviteSuccess(true);

          // Fire webhook to n8n (best-effort)
          try {
            await supabase.functions.invoke("send-invite-webhook", {
              body: {
                email: newOrgOwnerEmail.trim(),
                org_name: newOrgName.trim(),
                token: invData.token,
                onboarding_link: link,
              },
            });
            setInviteEmailSent(true);
          } catch (webhookErr) {
            console.error("Webhook notification failed (non-critical):", webhookErr);
            setInviteEmailSent(false);
          }
        }
      }

      setNewOrgOpen(false);
      setNewOrgName(""); setNewOrgNit(""); setNewOrgOwnerEmail("");
      refetchOrgs();
      queryClient.invalidateQueries({ queryKey: ["global-stats"] });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "No se pudo crear la empresa.", variant: "destructive" });
    }
    setCreatingOrg(false);
  };

  const handleCopyLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      toast({ title: "✅ Link copiado", description: "El enlace de invitación fue copiado al portapapeles." });
    }
  };

  return (
    <Tabs defaultValue="dashboard" className="space-y-6">
      <TabsList className="bg-muted/50">
        <TabsTrigger value="dashboard" className="gap-1.5"><Globe className="h-3.5 w-3.5" />Dashboard Global</TabsTrigger>
        <TabsTrigger value="empresas" className="gap-1.5"><Building2 className="h-3.5 w-3.5" />Empresas</TabsTrigger>
        <TabsTrigger value="usuarios" className="gap-1.5"><Users className="h-3.5 w-3.5" />Usuarios</TabsTrigger>
        <TabsTrigger value="super-admins" className="gap-1.5"><Crown className="h-3.5 w-3.5" />Super Admins</TabsTrigger>
        <TabsTrigger value="herramientas" className="gap-1.5"><FileText className="h-3.5 w-3.5" />Herramientas</TabsTrigger>
      </TabsList>

      {/* ── TAB: Dashboard Global ── */}
      <TabsContent value="dashboard" className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Empresas", value: stats?.total_orgs ?? "—", icon: Building2, color: "text-primary" },
            { label: "Usuarios Activos", value: stats?.total_users ?? "—", icon: Users, color: "text-primary" },
            { label: "Documentos", value: stats?.total_docs ?? "—", icon: FileText, color: "text-secondary" },
            { label: "Embarques", value: stats?.total_embarques ?? "—", icon: Ship, color: "text-primary" },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{label}</p>
                    <p className="text-2xl font-bold text-foreground">{value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick create user */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-border lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" />Crear Usuario</CardTitle>
              <CardDescription>Invita un nuevo cliente a la plataforma</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">Email</Label>
                  <Input type="email" placeholder="cliente@empresa.com" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Contraseña temporal</Label>
                  <Input type="password" placeholder="Mínimo 6 caracteres" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
                </div>
                <Button type="submit" disabled={creating} className="w-full gap-2">
                  <UserPlus className="h-4 w-4" />{creating ? "Creando..." : "Crear Usuario"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-border lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Usuarios (Auth)</CardTitle>
                  <CardDescription>{legacyUsers.length} usuario(s)</CardDescription>
                </div>
                <Button variant="outline" size="icon" onClick={fetchLegacyUsers} disabled={loadingLegacy}>
                  <RefreshCw className={`h-4 w-4 ${loadingLegacy ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-border overflow-auto max-h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead className="text-center">Créditos</TableHead>
                      <TableHead className="text-center">Docs</TableHead>
                      <TableHead>Creado</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingLegacy ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
                    ) : legacyUsers.map(u => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col leading-tight">
                            <span>{displayName(u)}</span>
                            <span className="text-xs text-muted-foreground tabular-nums">{u.email}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{u.credits_this_month}</TableCell>
                        <TableCell className="text-center">{u.total_documents}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatDate(u.created_at)}</TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                                <AlertDialogDescription>Se eliminará permanentemente a <strong>{u.email}</strong>.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(u.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  {deletingId === u.id ? "Eliminando..." : "Eliminar"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* ── TAB: Empresas ── */}
      <TabsContent value="empresas" className="space-y-4 animate-fade-in">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Organizaciones</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetchOrgs()} disabled={orgsLoading}>
              <RefreshCw className={`h-4 w-4 ${orgsLoading ? "animate-spin" : ""}`} />
            </Button>
            <Dialog open={newOrgOpen} onOpenChange={setNewOrgOpen}>
              <DialogTrigger asChild>
                <Button className="gap-1.5"><Plus className="h-4 w-4" />Nueva Empresa</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Nueva Empresa</DialogTitle>
                  <DialogDescription>Registra una nueva organización en la plataforma.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input placeholder="Mi Empresa S.A.S." value={newOrgName} onChange={e => setNewOrgName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>NIT</Label>
                    <Input placeholder="900.123.456-7" value={newOrgNit} onChange={e => setNewOrgNit(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email del Dueño</Label>
                    <Input type="email" placeholder="admin@empresa.com (debe existir)" value={newOrgOwnerEmail} onChange={e => setNewOrgOwnerEmail(e.target.value)} />
                    <p className="text-xs text-muted-foreground">Si se deja vacío, se te asignará a ti como Admin.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewOrgOpen(false)}>Cancelar</Button>
                  <Button onClick={handleCreateOrg} disabled={creatingOrg || !newOrgName.trim()}>
                    {creatingOrg ? "Creando..." : "Crear Empresa"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="border-border">
          <CardContent className="pt-4">
            <div className="rounded-md border border-border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>NIT</TableHead>
                    <TableHead className="text-center">Plan</TableHead>
                    <TableHead className="min-w-[180px]">Consumo Mensual</TableHead>
                    <TableHead className="text-center">Miembros</TableHead>
                    <TableHead>Creada</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgsLoading ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
                  ) : orgs.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No hay organizaciones.</TableCell></TableRow>
                  ) : orgs.map(org => {
                    const used = docCounts[org.id] ?? 0;
                    const limit = org.monthly_doc_limit || 100;
                    const pct = Math.min((used / limit) * 100, 100);
                    const planInfo = PLAN_LABELS[org.plan_type] ?? PLAN_LABELS.BASIC;
                    const isWarning = pct >= 90;
                    return (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          {org.nombre}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{org.nit || "—"}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={planInfo.className}>
                          {planInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Progress
                            value={pct}
                            className={`h-2 bg-muted ${isWarning ? "[&>div]:bg-amber-500" : "[&>div]:bg-primary"}`}
                          />
                          <p className={`text-[11px] tabular-nums ${isWarning ? "text-amber-400 font-medium" : "text-muted-foreground"}`}>
                            {used} / {limit} docs {isWarning && "⚠"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{org.member_count}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(org.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-primary"
                                disabled={changingPlanId === org.id}
                              >
                                <Crown className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {Object.entries(PLAN_LABELS).map(([key, info]) => (
                                <DropdownMenuItem
                                  key={key}
                                  onClick={() => handleChangePlan(org.id, key)}
                                  disabled={org.plan_type === key}
                                  className="gap-2"
                                >
                                  <Badge variant="outline" className={`${info.className} text-[10px]`}>{info.label}</Badge>
                                  <span className="text-xs text-muted-foreground">{PLAN_LIMITS[key]} docs/mes</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-primary"
                            onClick={() => setEditOrgTarget(org)}
                            title="Editar empresa"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => { setDeleteOrgTarget(org); setDeleteConfirmText(""); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Org Modal */}
        {editOrgTarget && (
          <OrgSettingsModal
            open={!!editOrgTarget}
            onOpenChange={(o) => { if (!o) { setEditOrgTarget(null); refetchOrgs(); } }}
            orgId={editOrgTarget.id}
            initialNombre={editOrgTarget.nombre}
            initialNit={editOrgTarget.nit}
          />
        )}

        {/* Delete Org Confirmation Dialog */}
        <Dialog open={!!deleteOrgTarget} onOpenChange={(open) => { if (!open) { setDeleteOrgTarget(null); setDeleteConfirmText(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Organización
              </DialogTitle>
              <DialogDescription>
                Esta acción es <strong>irreversible</strong>. Se borrarán <strong>todos</strong> los documentos, embarques, inventario y datos de <strong>{deleteOrgTarget?.nombre}</strong> permanentemente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label className="text-sm">
                Escribe <span className="font-mono font-bold text-destructive">{expectedDeleteText}</span> para confirmar:
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={expectedDeleteText}
                className="font-mono"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteOrgTarget(null); setDeleteConfirmText(""); }}>Cancelar</Button>
              <Button
                variant="destructive"
                disabled={deleteConfirmText !== expectedDeleteText || deletingOrg}
                onClick={handleDeleteOrg}
              >
                {deletingOrg ? "Eliminando..." : "Eliminar Permanentemente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite Link Success Dialog */}
        <Dialog open={showInviteSuccess} onOpenChange={(open) => { if (!open) { setShowInviteSuccess(false); setInviteLink(null); setInviteEmailSent(false); setInviteEmail(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Empresa Creada Exitosamente
              </DialogTitle>
              <DialogDescription>
                {inviteEmailSent
                  ? `✅ Se ha enviado un correo de invitación a ${inviteEmail}.`
                  : "Comparte este enlace de invitación con el administrador de la empresa."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">
                {inviteEmailSent
                  ? "En caso de que el correo no llegue, puedes copiar el enlace manualmente:"
                  : "Enlace de Invitación:"}
              </p>
              <div className="flex gap-2">
                <Input value={inviteLink || ""} readOnly className="bg-muted/50 text-xs font-mono" />
                <Button variant="outline" size="sm" onClick={handleCopyLink} className="shrink-0">
                  Copiar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Este enlace expira en 7 días. El invitado podrá crear su cuenta y unirse a la organización.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => { setShowInviteSuccess(false); setInviteLink(null); setInviteEmailSent(false); setInviteEmail(""); }}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TabsContent>

      {/* ── TAB: Usuarios Globales ── */}
      <TabsContent value="usuarios" className="space-y-4 animate-fade-in">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Usuarios Globales</h2>
          <Button variant="outline" size="icon" onClick={() => refetchUsers()} disabled={usersLoading}>
            <RefreshCw className={`h-4 w-4 ${usersLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <Card className="border-border">
          <CardContent className="pt-4">
            <div className="rounded-md border border-border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Vinculado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersLoading ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
                  ) : globalUsers.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No hay usuarios.</TableCell></TableRow>
                  ) : globalUsers.map((u, i) => (
                    <TableRow key={`${u.user_id}-${i}`}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col leading-tight">
                          <span>{displayName(u)}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">{u.email}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.org_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          u.rol === "ADMIN" ? "bg-primary/15 text-primary border-primary/30" :
                          u.rol === "OPERADOR" ? "bg-amber-500/15 text-amber-500 border-amber-500/30" :
                          "bg-muted text-muted-foreground border-border"
                        }>{u.rol}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(u.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* ── TAB: Super Admins ── */}
      <TabsContent value="super-admins" className="space-y-4 animate-fade-in">
        <SuperAdminsTab callerId={user!.id} />
      </TabsContent>

      {/* ── TAB: Herramientas ── */}
      <TabsContent value="herramientas" className="animate-fade-in">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CsvUploadCard />
          <CsvUploadReglasCard />
        </div>
      </TabsContent>
    </Tabs>
  );
}

/* ═══════════════════════════════════════════════════
   ORG ADMIN VIEW (non-super-admin)
   ═══════════════════════════════════════════════════ */
function OrgAdminView() {
  const { orgId, userRole } = useAuth();

  if (userRole !== "ADMIN") {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No tienes permisos de administrador.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />Mi Organización
          </CardTitle>
          <CardDescription>Gestiona tu equipo desde la página de Equipo en el menú lateral.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Como administrador de tu organización, puedes invitar y gestionar miembros desde la sección <strong>Mi Equipo</strong>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN ADMIN PANEL
   ═══════════════════════════════════════════════════ */
const AdminPanel = () => {
  const { user, userRole } = useAuth();
  const { isSuperAdmin, isLoading } = useIsSuperAdmin();

  if (!user) return <Navigate to="/dashboard/resumen" replace />;

  // While checking super admin status
  if (isLoading) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Verificando permisos...</p>
        </div>
      </AppShell>
    );
  }

  // Non-super-admin, non-org-admin → redirect
  if (!isSuperAdmin && userRole !== "ADMIN") {
    return <Navigate to="/dashboard/resumen" replace />;
  }

  return (
    <AppShell>
      <header className="h-14 flex items-center justify-between border-b border-border px-4 shrink-0">
        <div className="flex items-center gap-3">
          <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
          <Shield className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold text-foreground truncate">
            {isSuperAdmin ? "Consola Master" : "Administración"}
          </h1>
          {isSuperAdmin && (
            <Badge variant="outline" className="bg-primary/15 text-primary border-primary/30 text-[10px]">
              Super Admin
            </Badge>
          )}
        </div>
      </header>
      <main className="flex-1 p-6 overflow-auto">
        {isSuperAdmin ? <SuperAdminView /> : <OrgAdminView />}
      </main>
    </AppShell>
  );
};

export default AdminPanel;

/* ═══════════════════════════════════════════════════
   SUPER ADMINS TAB
   ═══════════════════════════════════════════════════ */
interface SuperAdminRow {
  user_id: string;
  email: string | null;
  full_name: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
}

function SuperAdminsTab({ callerId }: { callerId: string }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<SuperAdminRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [demoteTarget, setDemoteTarget] = useState<SuperAdminRow | null>(null);
  const [demoting, setDemoting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users?action=list-super-admins", { method: "GET" });
      if (error) throw error;
      setRows(data?.super_admins ?? []);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "No se pudieron cargar los Super Admins.", variant: "destructive" });
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handlePromote = async () => {
    if (!newEmail.trim()) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users?action=promote-super-admin", {
        body: { email: newEmail.trim(), password: newPassword.trim() || undefined },
      });
      if (error) throw error;
      if (data?.error) {
        toast({ title: "Error", description: data.error, variant: "destructive" });
      } else {
        toast({ title: "👑 Super Admin creado", description: data.message });
        setPromoteOpen(false);
        setNewEmail(""); setNewPassword("");
        fetchRows();
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "No se pudo promover.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const handleDemote = async () => {
    if (!demoteTarget) return;
    setDemoting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users?action=demote-super-admin", {
        body: { user_id: demoteTarget.user_id },
      });
      if (error) throw error;
      if (data?.error) {
        toast({ title: "Error", description: data.error, variant: "destructive" });
      } else {
        toast({ title: "Rol revocado", description: data.message });
        setDemoteTarget(null);
        fetchRows();
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "No se pudo revocar.", variant: "destructive" });
    }
    setDemoting(false);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" /> Super Administradores
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Acceso total al sistema con todas las funcionalidades Enterprise desbloqueadas. No pertenecen a ninguna organización.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchRows} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button onClick={() => setPromoteOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" /> Nuevo Super Admin
          </Button>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="pt-4">
          <div className="rounded-md border border-border overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Último acceso</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No hay Super Admins registrados.</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.user_id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col leading-tight">
                        <span className="flex items-center gap-1.5">
                          {displayName(r)}
                          {r.user_id === callerId && (
                            <Badge variant="outline" className="text-[10px] border-primary/40 text-primary bg-primary/10 px-1.5 py-0">Tú</Badge>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">{r.email ?? "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(r.last_sign_in_at)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={r.user_id === callerId}
                        onClick={() => setDemoteTarget(r)}
                        title={r.user_id === callerId ? "No puedes revocarte tu propio rol" : "Revocar rol Super Admin"}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Revocar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Promote modal */}
      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" /> Nuevo Super Administrador
            </DialogTitle>
            <DialogDescription>
              Si el email ya existe como usuario, será promovido (y removido de cualquier organización). Si no existe, se creará con la contraseña que indiques.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="sa-email">Email</Label>
              <Input
                id="sa-email"
                type="email"
                placeholder="backup@kargonode.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sa-pwd">Contraseña <span className="text-xs text-muted-foreground">(solo si el usuario no existe — min 8)</span></Label>
              <Input
                id="sa-pwd"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteOpen(false)} disabled={submitting}>Cancelar</Button>
            <Button onClick={handlePromote} disabled={submitting || !newEmail.trim()}>
              {submitting ? "Procesando..." : "Promover a Super Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Demote confirm */}
      <AlertDialog open={!!demoteTarget} onOpenChange={(o) => !o && setDemoteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revocar rol Super Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{demoteTarget?.email}</strong> perderá el acceso de Super Admin. La cuenta permanece activa pero sin pertenencia a ninguna organización (deberás invitarlo a una si quieres que siga operando).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={demoting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDemote} disabled={demoting} className="bg-destructive hover:bg-destructive/90">
              {demoting ? "Revocando..." : "Sí, revocar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
